import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { CoachAvatars } from "./_shared";
import { ChevronLeft, Users, Sparkles, ChevronRight, Loader2 } from "lucide-react";

export default function CollabProjects() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("client_collab_projects_title"));
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/client/collab/projects"); setProjects(data?.projects || []); }
    catch { setProjects([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="client-collab-projects">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/collab")} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("client_collab_projects_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("client_collab_projects_sub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : projects.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("client_collab_projects_empty")}</p>
      ) : projects.map((p) => {
        const isCircle = p.kind === "circle";
        const accent = isCircle ? "#4B4B7A" : "#3E7C6A";
        return (
          <button key={`${p.kind}-${p.id}`} data-testid={`client-collab-project-${p.id}`} onClick={() => navigate(`/app/collab/project/${p.kind}/${p.id}`)}
            className="flex w-full gap-3 overflow-hidden rounded-2xl border border-border bg-secondary/60 text-left transition-colors hover:bg-accent">
            <span className="w-1 shrink-0" style={{ backgroundColor: accent }} />
            <div className="min-w-0 flex-1 py-3.5">
              <div className="flex flex-wrap items-center gap-1.5">
                {isCircle ? <Users className="h-3.5 w-3.5" style={{ color: accent }} /> : <Sparkles className="h-3.5 w-3.5" style={{ color: accent }} />}
                <span className="truncate font-display text-sm text-foreground">{p.title}</span>
                <span className="rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase" style={{ backgroundColor: accent + "22", color: accent }}>{isCircle ? t("client_collab_kind_circle") : t("client_collab_kind_offering")}</span>
              </div>
              {p.description ? <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.description}</p> : null}
              <div className="mt-2"><CoachAvatars coaches={p.coaches} size={26} /></div>
              <p className="mt-1 truncate text-[11px] text-muted-foreground">{p.coaches.map((c) => c.name).join(" · ")}</p>
            </div>
            <ChevronRight className="my-auto mr-3 h-5 w-5 shrink-0 text-muted-foreground" />
          </button>
        );
      })}
    </div>
  );
}
